import React, { useCallback, useContext, useRef, useState } from 'react';
import { useNameObjectId } from 'corp/hooks/smartId/useNameObjectId';
import { INamedObjectTypes, INamedObjectId } from '@scale/shared/types/lookup';
import UserPreview from 'corp/components/SmartLink/UserPreview';
import PermissionGroupPreview from 'corp/components/SmartLink/PermissionGroupPreview';
import CoursePreview from 'corp/components/SmartLink/CoursePreview';
import ProjectPreview from 'corp/components/SmartLink/ProjectPreview';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import SubtaskPreview from 'corp/components/SmartLink/SubtaskPreview';
import Portal from 'corp/components/SmartLink/PreviewPortal';
import TaskPreview from 'corp/components/SmartLink/TaskPreview';
import TaskAttemptPreview from 'corp/components/SmartLink/TaskAttemptPreview';
import TaskReviewPreview from 'corp/components/SmartLink/TaskReviewPreview';
import TrainingAttemptPreview from 'corp/components/SmartLink/TrainingAttemptPreview';
import TrainingScenarioPreview from 'corp/components/SmartLink/TrainingScenarioPreview';
import TagPreview from 'corp/components/SmartLink/TagPreview';
import PersonaPreview from 'corp/components/SmartLink/PersonaPreview';
import ProjectSLAGroupPreview from 'corp/components/SmartLink/ProjectSLAGroupPreview';
import ProjectCollectionPreview from 'corp/components/SmartLink/ProjectCollectionPreview';
import PipelineV3ManualInterventionPreview from 'corp/components/SmartLink/PipelineV3ManualInterventionPreview';
import RemoAdminContext from 'react-scale/components/remoAdmin/remoAdminContext';
import { IconButton } from '@mui/material';
import WorkerTeamPreview from 'react-scale/corp/components/SmartLink/WorkerTeamPreview';
import AudiencePreview from 'react-scale/corp/components/SmartLink/AudiencePreview';
import OTSSubscriptionPreview from 'react-scale/corp/components/SmartLink/OTSSubscriptionPreview';
import OTSDeliveredBatchPreview from 'react-scale/corp/components/SmartLink/OTSDeliveredBatchPreview';
import OTSDeliveredTaskPreview from 'react-scale/corp/components/SmartLink/OTSDeliveredTaskPreview';
import OTSTaskPreview from 'react-scale/corp/components/SmartLink/OTSTaskPreview';
import OTSUploadBatchPreview from 'react-scale/corp/components/SmartLink/OTSUploadBatchPreview';
import ModelRequestAndResponsePreview from 'react-scale/corp/components/SmartLink/ModelRequestAndResponsePreview';

export interface CustomQuickAction {
  title: string;
  onClick: () => void;
}

interface ISmartLink {
  type: INamedObjectTypes;
  id: string;
  linkText?: string;
  showLogo?: boolean;
  customQuickAction?: CustomQuickAction;
  onDelete?: () => void;
}

const SmartLinkBadge: React.FC<{ type: INamedObjectTypes; imgSrc?: INamedObjectId }> = ({
  type,
  imgSrc,
}) => {
  const codes = {
    [INamedObjectTypes.Project]: 'P',
    [INamedObjectTypes.User]: 'U',
    [INamedObjectTypes.Subtask]: 'S',
    [INamedObjectTypes.Course]: 'C',
    [INamedObjectTypes.Task]: 'T',
    [INamedObjectTypes.PermissionGroup]: 'PG',
    [INamedObjectTypes.TaskAttempt]: 'TA',
    [INamedObjectTypes.TaskReview]: 'TR',
    [INamedObjectTypes.TrainingAttempt]: 'TRA',
    [INamedObjectTypes.TrainingScenario]: 'BM',
    [INamedObjectTypes.IncompleteTaskAttempt]: 'ITA',
    [INamedObjectTypes.Tag]: 'TAG',
    [INamedObjectTypes.Persona]: 'PER',
    [INamedObjectTypes.ProjectSLAGroup]: 'PG',
    [INamedObjectTypes.ProjectCollection]: 'Pc',
    [INamedObjectTypes.PipelineV3ManualIntervention]: 'TPV3M',
    [INamedObjectTypes.AccountVerification]: 'AV',
    [INamedObjectTypes.Redirect]: 'Re',
    [INamedObjectTypes.WorkerTeam]: 'WT',
    [INamedObjectTypes.Stage]: 'St',
    [INamedObjectTypes.Substage]: 'SSt',
    [INamedObjectTypes.Audience]: 'A',
    [INamedObjectTypes.Qualification]: 'QUAL',
    [INamedObjectTypes.QAOperation]: 'QAOp',
    [INamedObjectTypes.OTSSubscription]: 'OTSSub',
    [INamedObjectTypes.OTSDeliveredBatch]: 'OTSBatch',
    [INamedObjectTypes.OTSDeliveredTask]: 'OTSDeliveredTask',
    [INamedObjectTypes.OTSTask]: 'OTSTask',
    [INamedObjectTypes.OTSUploadBatch]: 'UploadBatch',
    [INamedObjectTypes.ModelRequestAndResponse]: 'ModelReqResp',
  };
  const colors = {
    [INamedObjectTypes.Project]: 'var(--color-ScaleBlue)',
    [INamedObjectTypes.User]: 'var(--color-RemoBlue)',
    [INamedObjectTypes.Subtask]: 'var(--color-RemoGreen)',
    [INamedObjectTypes.Task]: 'var(--color-RemoGold)',
    [INamedObjectTypes.PermissionGroup]: 'var(--color-RemoRed)',
    [INamedObjectTypes.Course]: 'var(--color-LightPeriwinkle)',
    [INamedObjectTypes.TaskAttempt]: 'var(--color-LightMauve)',
    [INamedObjectTypes.TaskReview]: 'var(--color-sensor-fusion-cuboids)',
    [INamedObjectTypes.TrainingAttempt]: 'var(--color-SkyBlue)',
    [INamedObjectTypes.TrainingScenario]: 'var(--color-ScaleBlue)',
    [INamedObjectTypes.IncompleteTaskAttempt]: 'var(--color-LightMauve)',
    [INamedObjectTypes.Tag]: 'var(--color-LightPeriwinkle)',
    [INamedObjectTypes.Persona]: 'var(--color-RemoGreen)',
    [INamedObjectTypes.ProjectSLAGroup]: 'var(--color-orange)',
    [INamedObjectTypes.ProjectCollection]: 'var(--color-LightSkyBlue)',
    [INamedObjectTypes.PipelineV3ManualIntervention]: 'var(--color-RemoGreen)',
    [INamedObjectTypes.AccountVerification]: 'var(--color-LightPeriwinkle)',
    [INamedObjectTypes.Redirect]: 'var(--color-LightPeriwinkle)',
    [INamedObjectTypes.WorkerTeam]: 'var(--color-ScaleBlue)',
    [INamedObjectTypes.Stage]: 'var(--color-RemoGold)',
    [INamedObjectTypes.Substage]: 'var(--color-RemoGreen)',
    [INamedObjectTypes.Audience]: 'var(--color-ScaleBlue)',
    [INamedObjectTypes.Qualification]: 'var(--color-ScaleBlue)',
    [INamedObjectTypes.QAOperation]: 'var(--color-ScaleBlue)',
    [INamedObjectTypes.OTSSubscription]: 'var(--color-ScaleBlue)',
    [INamedObjectTypes.OTSDeliveredBatch]: 'var(--color-ScaleBlue)',
    [INamedObjectTypes.OTSDeliveredTask]: 'var(--color-ScaleBlue)',
    [INamedObjectTypes.OTSTask]: 'var(--color-ScaleBlue)',
    [INamedObjectTypes.OTSUploadBatch]: 'var(--color-ScaleBlue)',
    [INamedObjectTypes.ModelRequestAndResponse]: 'var(--color-ScaleBlue)',
  };
  return (
    <span
      style={{
        color: 'white',
        background: colors[type],
        height: '32px',
        alignItems: 'center',
        borderRadius: '16px 0 0 16px',
        padding: '0 7px 0 10px',
        lineHeight: '32px',
      }}
    >
      {imgSrc ? <img src={imgSrc.logo} /> : codes[type] ?? '?'}
    </span>
  );
};

// eslint-disable-next-line complexity
const SmartLink: React.FC<ISmartLink> = ({
  id,
  type,
  linkText,
  showLogo = true,
  customQuickAction,
  onDelete,
}) => {
  const { value, loading, error } = useNameObjectId(id, type);
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);
  const [coords, setCoords] = useState<DOMRect | undefined>();
  const close = useCallback(
    (e: MouseEvent) => {
      setOpen(false);
      e.stopPropagation();
    },
    [setOpen],
  );
  const remoAdminCtx = useContext(RemoAdminContext);

  const openPreview = useCallback(
    (e: $FIXME) => {
      // Determine the top left corner of our bounding box
      let target: HTMLElement | null = e.target as HTMLDivElement;
      while (target) {
        if ([...(target.classList || [])].includes(smartLinkClass)) break;
        target = target.parentElement;
      }
      if (!target) return; // Unable to determine container element?

      setOpen(true);
      setCoords(target.getBoundingClientRect());
    },
    [setOpen, setCoords],
  );

  if (!id) return <span />;

  const monospaceTypes = [
    INamedObjectTypes.Subtask,
    INamedObjectTypes.TaskAttempt,
    INamedObjectTypes.TaskReview,
  ];

  const wrapperOfType = (children: any) =>
    monospaceTypes.includes(type) ? <pre>{children}</pre> : children;

  const smartLinkClass = 'smart-link';
  return (
    <span ref={wrapperRef} className={smartLinkClass} onClick={openPreview}>
      <style jsx>{`
        .smart-link {
          border: 1px solid #ccc;
          border-radius: 3px;
          cursor: pointer;
          display: inline-flex;
          height: 32px;
          border-radius: 16px;
          background: #eee;
          align-items: center;
          transform-style: preserve-3d;
          margin-right: 10px;
          overflow: hidden;
        }

        .smart-link__inner {
          padding: 3px ${onDelete ? 10 : 20}px 3px 15px;
          color: black;
        }

        .smart-link:hover .smart-link__inner {
        }

        .smart-preview {
          position: absolute;
          bottom: 32px;
          left: 0;
          border: 1px solid #ccc;
          opacity: 0.95;
          padding: 10px;
          box-shadow: 3px 3px 3px #eee;
          border-radius: 5px;
          cursor: default;
          color: black;
          z-index: 1009;
          width: auto;
          background: white;
          overflow-wrap: break-word;
          word-wrap: break-word;
          z-index: 9999;
        }

        .smart-preview__close {
          color: #888;
          position: absolute;
          top: 5px;
          right: 5px;
          font-size: 20px;
        }

        .smart-preview__close:hover {
          color: black;
          cursor: pointer;
        }
      `}</style>
      {showLogo &&
        (value?.logo ? (
          <img style={{ width: '30px' }} src={value?.logo} />
        ) : (
          <SmartLinkBadge type={type} />
        ))}
      {linkText ? (
        <div className="smart-link__inner">{linkText}</div>
      ) : (
        <span className="smart-link__inner">
          {wrapperOfType(
            loading || error || (remoAdminCtx.hidePII && type === INamedObjectTypes.User)
              ? id
              : value?.name,
          )}
        </span>
      )}
      {onDelete && (
        <IconButton onClick={() => onDelete()} style={{ marginRight: '12px' }}>
          <FontAwesomeIcon icon="times" />
        </IconButton>
      )}
      {open && coords && (
        <Portal onOutsideClick={() => setOpen(false)}>
          <div
            className="smart-preview"
            style={
              coords
                ? {
                    bottom: window.innerHeight - window.scrollY - coords.top + 'px',
                    left: coords.left + 'px',
                  }
                : {}
            }
          >
            <div onClick={(e: $FIXME) => close(e)} className="smart-preview__close">
              <FontAwesomeIcon icon="times" />
            </div>
            {type === INamedObjectTypes.User ? <UserPreview id={id} /> : ''}
            {type === INamedObjectTypes.Subtask ? (
              <SubtaskPreview id={id} customQuickAction={customQuickAction} />
            ) : (
              ''
            )}
            {type === INamedObjectTypes.TaskAttempt ? <TaskAttemptPreview id={id} /> : ''}
            {type === INamedObjectTypes.TaskReview ? <TaskReviewPreview id={id} /> : ''}
            {type === INamedObjectTypes.Task ? (
              <TaskPreview id={id} customQuickAction={customQuickAction} />
            ) : (
              ''
            )}
            {type === INamedObjectTypes.PermissionGroup ? <PermissionGroupPreview id={id} /> : ''}
            {type === INamedObjectTypes.Project ? <ProjectPreview id={id} /> : ''}
            {type === INamedObjectTypes.Course ? <CoursePreview id={id} /> : ''}
            {type === INamedObjectTypes.TrainingAttempt ? <TrainingAttemptPreview id={id} /> : ''}
            {type === INamedObjectTypes.PipelineV3ManualIntervention ? (
              <PipelineV3ManualInterventionPreview id={id} />
            ) : (
              ''
            )}
            {type === INamedObjectTypes.TrainingScenario ? <TrainingScenarioPreview id={id} /> : ''}
            {type === INamedObjectTypes.Tag ? <TagPreview id={id} /> : ''}
            {type === INamedObjectTypes.Persona ? <PersonaPreview id={id} /> : ''}
            {type === INamedObjectTypes.ProjectSLAGroup ? <ProjectSLAGroupPreview id={id} /> : ''}
            {type === INamedObjectTypes.ProjectCollection ? (
              <ProjectCollectionPreview id={id} />
            ) : (
              ''
            )}
            {type === INamedObjectTypes.WorkerTeam ? <WorkerTeamPreview id={id} /> : ''}
            {type === INamedObjectTypes.Audience ? <AudiencePreview id={id} /> : ''}
            {type === INamedObjectTypes.OTSSubscription ? <OTSSubscriptionPreview id={id} /> : ''}
            {type === INamedObjectTypes.OTSDeliveredBatch ? (
              <OTSDeliveredBatchPreview id={id} />
            ) : (
              ''
            )}
            {type === INamedObjectTypes.OTSDeliveredTask ? <OTSDeliveredTaskPreview id={id} /> : ''}
            {type === INamedObjectTypes.OTSTask ? <OTSTaskPreview id={id} /> : ''}
            {type === INamedObjectTypes.OTSUploadBatch ? <OTSUploadBatchPreview id={id} /> : ''}
            {type === INamedObjectTypes.ModelRequestAndResponse ? (
              <ModelRequestAndResponsePreview id={id} />
            ) : (
              ''
            )}
          </div>
        </Portal>
      )}
    </span>
  );
};

export default SmartLink;
